import { alpha, type Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'

/** Concave corner size — Chrome's inverted bottom radii. */
export const CHROME_TAB_RADIUS_PX = 8

export function chromeTabStripBg(theme: Theme): string {
  return alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.1 : 0.07)
}

/** Chrome-shaped workspace tabs: selected pours into paper, inactive get hairline separators. */
export function chromeTabBarSx(theme: Theme): SystemStyleObject<Theme> {
  const r = CHROME_TAB_RADIUS_PX
  const strip = chromeTabStripBg(theme)
  const paper = theme.palette.background.paper
  return {
    bgcolor: strip,
    borderBottom: 'none',
    mb: 0,
    pt: 0.5,
    px: 0.5,
    overflow: 'visible',
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTabs-flexContainer': {
      alignItems: 'flex-end',
      gap: '2px',
      px: `${r}px`,
    },
    '& .MuiTab-root': {
      position: 'relative',
      minHeight: 32,
      maxWidth: 280,
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 500,
      py: 0,
      px: 1.5,
      opacity: 1,
      overflow: 'visible',
      color: 'text.secondary',
      bgcolor: 'transparent',
      borderRadius: `${r}px ${r}px 0 0`,
      zIndex: 0,
      '&:not(.Mui-selected):hover': {
        bgcolor: alpha(theme.palette.text.primary, 0.05),
      },
      // Hairline between two inactive tabs; hidden next to the selected one.
      '&:not(.Mui-selected):not(:last-of-type):not(:has(+ .Mui-selected))::after': {
        content: '""',
        position: 'absolute',
        right: -1,
        top: '22%',
        height: '56%',
        width: '1px',
        bgcolor: 'divider',
        pointerEvents: 'none',
      },
      '&.Mui-selected': {
        color: 'text.primary',
        bgcolor: paper,
        zIndex: 1,
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          width: r,
          height: r,
          pointerEvents: 'none',
        },
        '&::before': {
          left: -r,
          background: `radial-gradient(circle at 0 0, transparent ${r}px, ${paper} ${r}px)`,
        },
        '&::after': {
          right: -r,
          background: `radial-gradient(circle at 100% 0, transparent ${r}px, ${paper} ${r}px)`,
        },
      },
    },
  }
}
